<template>
  <NTable
    :data="data"
    :identifier="
      (row) =>
        `props.isPortfolioSelected ? ${row.instrumentIsin}: ${row.isin}-${row.instrumentId}`
    "
    :highlight-by="highlightActiveRowByIsin"
    cell-padding="small"
    :column-splitter="false"
    :striped="false"
    virtual-scroll
    fixed-header
    class="m-2"
    :sort-by="{
      prop: 'pricePercentage',
      order: 'descending',
    }"
    @row-click="(row) => handleRowClick(row)"
  >
    <template #hoveredElements="{ row }">
      <NPopper hover offset-y="5" placement="top" append-to-body>
        <NegarIndustryIcon
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'watchlist',
            component: 'industryGroupsIcon',
          }"
          size="20"
          class="m-2"
          @click="openIndustryGroups"
        />

        <template #content>
          <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
            <NText
              weight="medium"
              variant="caption2"
              class="text-neutral-quaternary"
            >
              {{ t('symbol.industryGroups') }}
            </NText>
          </div>
        </template>
      </NPopper>

      <NButton
        v-gid="{
          module: 'order',
          screen: 'home',
          section: 'watchlist',
          component: 'buyButton',
        }"
        size="mini"
        variant="fill"
        @click.stop="
          openNewOrderFormDialog({
            type: ORDER_TYPES.buy,
            symbol: row,
          })
        "
      >
        <NText variant="overline" weight="medium">
          {{ t('common.buy') }}
        </NText>
      </NButton>

      <NButton
        v-gid="{
          module: 'order',
          screen: 'home',
          section: 'watchlist',
          component: 'sellButton',
        }"
        size="mini"
        color="red"
        :disabled="offersIsin?.includes(row.isin)"
        variant="fill"
        class="mr-1"
        @click.stop="
          openNewOrderFormDialog({
            type: ORDER_TYPES.sell,
            symbol: row,
          })
        "
      >
        <NText variant="overline" weight="medium">
          {{ t('common.sell') }}
        </NText>
      </NButton>

      <NegarTrashLightIcon
        v-if="!isPortfolioSelected"
        v-gid="{
          module: 'watchlist',
          screen: 'home',
          section: 'watchlistSymbolsTable',
          component: 'deleteFromWatchlistButton',
        }"
        size="20"
        class="cursor-pointer text-critical-primary mr-2 ml-1"
        @click.stop="
          handleRemoveFromWatchlist(getSymbolIsin(row), row.instrumentName)
        "
      />
    </template>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.watchlist.symbol_name')"
      prop="instrumentName"
      sortable
      class="w-28"
      align="text-right"
    >
      <span class="inline-flex justify-center items-center">
        <SymbolTradeStateIcon
          class="me-1"
          :trading-state-code="row.instrumentTradingState"
        />
        <NText :key="row.instrumentName" variant="caption2" weight="medium">
          {{ row.instrumentName }}
        </NText>
      </span>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="PriceColumnLabel"
      prop="pricePercentage"
      class="w-28 inline-flex items-center"
      align="text-right"
      sortable
    >
      <!-- todo: use token color -->
      <span
        class="inline-flex items-center px-1 rounded-md"
        :class="{
          'bg-[#ffd420] bg-opacity-[15%] darkTheme:bg-opacity-10 darkTheme:bg-[#ffe449]':
            row.instrumentTradingState === TRADING_STATE_CODES.preOpening,
        }"
      >
        <template
          v-if="row.instrumentTradingState === TRADING_STATE_CODES.preOpening"
        >
          <NumberFormatter
            #="{ number }"
            :value="row.topVariationPercentage / 100"
            number-style="percent"
            maximum-fraction-digits="2"
          >
            <NText
              dir="ltr"
              variant="caption2"
              weight="bold"
              :class="{
                'text-brand-primary': row.topVariationPercentage > 0,
                'text-critical-primary': row.topVariationPercentage < 0,
              }"
              class="ms-1"
            >
              ({{ number }})
            </NText>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="row.theoricalOpeningPrice">
            <ChangePriceEffect :price="row.theoricalOpeningPrice">
              <NText
                :key="row.theoricalOpeningPrice"
                variant="caption2"
                weight="bold"
              >
                {{ number }}
              </NText>
            </ChangePriceEffect>
          </NumberFormatter>
        </template>

        <template v-else>
          <NumberFormatter
            #="{ number }"
            :value="row.referencePriceVariationPercentage / 100"
            number-style="percent"
            maximum-fraction-digits="2"
          >
            <NText
              dir="ltr"
              variant="caption2"
              weight="bold"
              :class="{
                'text-brand-primary': row.referencePriceVariationPercentage > 0,
                'text-critical-primary':
                  row.referencePriceVariationPercentage < 0,
              }"
              class="ms-1"
            >
              ({{ number }})
            </NText>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="row.lastTradePrice">
            <ChangePriceEffect :price="row.lastTradePrice">
              <NText :key="row.lastTradePrice" variant="caption2" weight="bold">
                {{ number }}
              </NText>
            </ChangePriceEffect>
          </NumberFormatter>
        </template>
      </span>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="`%${t('misc.final')}`"
      prop="closingPriceVariationPercentage"
      sortable
    >
      <NumberFormatter
        #="{ number }"
        :value="row.closingPriceVariationPercentage / 100"
        number-style="percent"
        maximum-fraction-digits="2"
      >
        <NText
          dir="ltr"
          variant="caption2"
          weight="bold"
          :class="{
            'text-brand-primary': row.closingPriceVariationPercentage > 0,
            'text-critical-primary': row.closingPriceVariationPercentage < 0,
          }"
        >
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>
  </NTable>
</template>

<script setup lang="ts">
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { NText, useToast } from '@negar/core';
import {
  InitialPublicOfferItem,
  PortfolioItem,
  WatchlistItem,
} from '@libs/api-reference';
import {
  NegarTrashLightIcon,
  NegarIndustryIcon,
  NegarInfoWithBorderIcon,
} from '@negar/icons';
import { getSymbolIsin, TRADING_STATE_CODES } from '@core/symbol';
import {
  useRemoveSymbolFromWatchlist,
  WATCHLIST_QUERY_KEYS,
} from '@core/watchlist';
import { ORDER_TYPES } from '@core/order';
import { useQueryClient } from '@libs/use';
import { dataLayer } from '@libs/gtm';

import { SymbolTradeStateIcon, useActiveSymbolIsin } from '@/modules/symbol';
import { useOrderDialogs } from '@/modules/order';
import { useWatchlistDialogs } from '@/modules/watchlist';

import ChangePriceEffect from '@/components/ChangePriceEffect.vue';
import NumberFormatter from '@/components/Number.vue';

import { openIndustryGroups } from '@/composables/event/eventBus';

const props = defineProps<{
  data: PortfolioItem[] | WatchlistItem[];
  isPortfolioSelected: boolean;
  selectedWatchlistId?: string;
  selectedWatchlistTitle?: string;
  offersIsin?: (string | null)[];
  offers?: InitialPublicOfferItem[];
}>();

const { t } = useI18n();
const toast = useToast();

const { openNewOrderFormDialog } = useOrderDialogs();
const { openWatchlistPriceDescriptionDialog } = useWatchlistDialogs();

const queryClient = useQueryClient();

const { activeIsin, updateActiveIsin } = useActiveSymbolIsin();

const highlightActiveRowByIsin = computed(() => ({
  prop: props.isPortfolioSelected ? 'instrumentIsin' : 'isin',
  value: activeIsin.value,
}));

const { removeSymbolFromWatchlist } = useRemoveSymbolFromWatchlist({
  onSuccess: (_, { instrumentName }) => {
    queryClient.invalidateQueries(
      WATCHLIST_QUERY_KEYS.watchlistItems(props.selectedWatchlistId)
    );
    queryClient.invalidateQueries(WATCHLIST_QUERY_KEYS.watchlistOverview);
    queryClient.invalidateQueries(WATCHLIST_QUERY_KEYS.watchlist);

    toast.neutral({
      message: t('modules.watchlist.removeSymbolFromWatchlistMessage', {
        symbolName: instrumentName,
        watchlistName: props.selectedWatchlistTitle,
      }),
      duration: 3000,
    });
  },
  onReject: (_, { instrumentName }) => {
    toast.negative({
      message: t('modules.watchlist.removeSymbolFromWatchlistFailedMessage', {
        symbolName: instrumentName,
        watchlistName: props.selectedWatchlistTitle,
      }),
      duration: 3000,
    });
  },
});

const handleRemoveFromWatchlist = (isin: string, name: string) => {
  removeSymbolFromWatchlist({
    watchlistId: props.selectedWatchlistId,
    isin: isin,
    instrumentName: name,
  });
};

const handleRowClick = (order) => {
  updateActiveIsin(getSymbolIsin(order));

  dataLayer.push({
    event: 'selectSymbolFromWatchlistTable',
  });
};

const PriceColumnLabel = h(
  'span',
  {
    class: 'inline-flex items-center align-middle',
  },
  [
    h(NegarInfoWithBorderIcon, {
      size: 14,
      class: 'me-0.5',
      'data-gid': 'watchlist_home_industry_priceInfo',
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
        openWatchlistPriceDescriptionDialog();
      },
    }),
    h(
      NText,
      {
        weight: 'bold',
        variant: 'overline',
      },
      [t('common.price')]
    ),
  ]
);
</script>
